/**
 * Supplier pricing engine (client side, for instant feedback).
 * Mirrors easybar_backend/supplier/pricing_engine.py — the server result is the source of truth
 * for saved prices (OrderItem.unit_price) and quotes.
 */
import { polygonSteelType, roundSteelType } from "../constants/rebar-constants";

export type ChargeType = "bend" | "cut" | "unit" | "none";
export type TransportMethod = "per_ton" | "fixed" | "none";

export interface DiameterPrice {
  diameter: number;
  ribbed: number | null;
  smooth: number | null;
  weldableExtra: number | null;
}

export interface ShapeException {
  id?: number;
  shape: number;
  shapeName?: string;
  shapeNumber?: string;
  chargeType: ChargeType;
  processingPerTon: number | null;
  unitPrice: number | null;
  customers: number[]; // empty = all customers
}

export interface PriceList {
  vatPercent: number;
  wastePercent: number;
  quoteValidityDays: number;
  cutPerTon: number | null;
  bendPerTon: number | null;
  unitPrice: number | null;
  minUnitPrice: number | null;
  shortShapeAsUnit: boolean;
  shortShapeMaxCm: number | null;
  transportMethod: TransportMethod;
  transportPrice: number | null;
  transportFreeAboveTons: number | null;
  transportMinimum: number | null;
  diameterPrices: DiameterPrice[];
  shapeExceptions: ShapeException[];
  lastModified?: string;
  updatedByName?: string | null;
}

export interface CustomerPricing {
  cutPerTon?: number | null;
  bendPerTon?: number | null;
  unitPrice?: number | null;
  customer: number;
  customerName?: string;
  mode: "default" | "custom";
  discountPercent: number;
  wastePercent: number | null;
  paymentTerms: string;
  transportMode: "pricelist" | "custom";
  transportPrice: number | null;
  transportFreeAboveTons: number | null;
  diameterOverrides: { diameter: number; pricePerTon: number | null }[];
  lastModified?: string;
}

export interface PricedItem {
  id: number;
  diameter: number | null;
  quantity: number;
  weightTon: number;
  chargeType: ChargeType;
  materialPerTon: number | null;
  material: number;
  processing: number;
  processingExact?: number;
  processingRate?: number | null;
  processingMissing?: boolean;
  billingQuantity?: number;
  shapeId?: number | null;
  shapeName?: string | null;
  shapeNumber?: string | null;
  rowNumber?: number;
  total: number;
  unitPrice: number;
  missingPrice: boolean;
}

export interface PricingResult {
  items: PricedItem[];
  byDiameter: { diameter: number | null; weightTon: number; material: number; bend: number; cut: number; units: number; total: number }[];
  totals: {
    weightTon: number;
    wasteTon: number;
    material: number;
    bend: number;
    cut: number;
    units: number;
    transport: number;
    discount: number;
    subtotal: number;
    vatPercent: number;
    vat: number;
    total: number;
    missingPrices: number[];
  };
}

// same list as save-preview-delete/order-totals
export const HOOP_REFERENCE_IDS = new Set<number>([8, 6, 94, 95, 96, 97, 98, 99, 101, 102, 103, 104, 105, 106, 199, 204, 205, 206, 207, 210]);
const STOCK_LENGTHS_CM = new Set<number>([600, 1200]);

const num = (v: unknown, d = 0): number => {
  if (v === null || v === undefined || v === "") return d;
  const n = Number(v);
  return Number.isFinite(n) ? n : d;
};
const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

interface NormalizedItem {
  id: number;
  kind: string;
  quantity: number;
  unitWeightKg: number;
  diameter: number | null;
  smooth: boolean;
  weldable: boolean;
  shapeId: number | null;
  totalLengthCm: number;
  hasBend: boolean;
  isHoop: boolean;
}

const diameterOf = (d: any): number | null => {
  if (d === null || d === undefined) return null;
  if (typeof d === "object") return d.steel_diameter !== undefined ? num(d.steel_diameter, NaN) || null : null;
  return num(d, NaN) || null;
};

/** EasyBar order item (rebar / net / pile from the redux store) → engine item. */
export function normalizeOrderItem(item: any): NormalizedItem {
  const tool = item?.tool || {};
  const kind = tool.subclass_type || "rebar";
  const sides: any[] = tool.rebar_side || tool.rebar_sides || [];
  const shapeId = num(tool.reference_tool ?? tool.id, NaN);
  let diameter: number | null = null;
  if (kind === "rebar") diameter = diameterOf(tool.diameter);
  else if (kind === "net") diameter = diameterOf(tool.x_diameter);
  else if (kind === "pile") {
    const main = tool.external_diameter ??
      (!tool.middle_diameter && !tool.internal_diameter ? tool.base_diameter : null);
    diameter = diameterOf(main);
  }

  return {
    id: item.id,
    kind,
    quantity: num(item.quantity),
    unitWeightKg: num(tool.unit_weight),
    diameter,
    smooth: tool.steel_type === roundSteelType,
    weldable: false, // The persisted EasyBar tool has no weldable flag; match the backend.
    shapeId: Number.isFinite(shapeId) ? shapeId : null,
    totalLengthCm: sides.reduce((s: number, x: any) => s + num(x?.length), 0),
    hasBend: sides.some((x: any) => x?.angle !== null && x?.angle !== undefined && num(x.angle) !== 180),
    isHoop: Number.isFinite(shapeId) && HOOP_REFERENCE_IDS.has(shapeId),
  };
}

export function materialPricePerTon(pl: PriceList, customer: CustomerPricing | null, it: NormalizedItem): number | null {
  if (it.diameter === null) return null;
  const override = customer?.mode === "custom" ? customer.diameterOverrides.find((o) => num(o.diameter) === it.diameter) : undefined;
  if (override?.pricePerTon != null) return num(override.pricePerTon);
  const row = pl.diameterPrices.find((r) => num(r.diameter) === it.diameter);
  if (!row) return null;
  const base = it.smooth ? row.smooth : row.ribbed;
  if (base === null || base === undefined) return null;
  return num(base); // Stored weldable surcharges are currently disabled.
}

export function findShapeException(pl: PriceList, shapeId: number | null, customerId: number | null): ShapeException | null {
  if (shapeId === null) return null;
  const list = pl.shapeExceptions.filter((e) => e.shape === shapeId);
  return (
    (customerId ? list.find((e) => e.customers.includes(customerId)) : undefined) ||
    list.find((e) => e.customers.length === 0) ||
    null
  );
}

export function resolveChargeType(it: NormalizedItem, pl: PriceList, customerId: number | null): ChargeType {
  const exc = findShapeException(pl, it.shapeId, customerId);
  if (exc) return exc.chargeType;
  if (it.kind !== "rebar") return "none";
  if (it.isHoop) return "unit";
  if (it.hasBend) return "bend";
  if (STOCK_LENGTHS_CM.has(Math.trunc(it.totalLengthCm))) return "none";
  return "cut";
}

export function priceItem(raw: any, pl: PriceList, customer: CustomerPricing | null, customerId: number | null): PricedItem {
  const it: NormalizedItem = raw.unitWeightKg !== undefined ? raw : normalizeOrderItem(raw);
  const weightTon = (it.unitWeightKg * it.quantity) / 1000;
  const perTon = materialPricePerTon(pl, customer, it);
  const charge = resolveChargeType(it, pl, customerId);
  const exc = findShapeException(pl, it.shapeId, customerId);
  const excOr = (v: number | null | undefined, fallback: number | null) => (v !== null && v !== undefined ? num(v) : num(fallback));

  const custom = customer?.mode === "custom" ? customer : null;
  const bendRate = custom?.bendPerTon ?? pl.bendPerTon;
  const cutRate = custom?.cutPerTon ?? pl.cutPerTon;
  const unitRate = custom?.unitPrice ?? pl.unitPrice;
  let processing = 0;
  if (charge === "bend") processing = weightTon * excOr(exc?.processingPerTon, bendRate);
  else if (charge === "cut") processing = weightTon * excOr(exc?.processingPerTon, cutRate);
  else if (charge === "unit") processing = it.quantity * excOr(exc?.unitPrice, unitRate);

  const processingMissing =
    (charge === "bend" && exc?.processingPerTon == null && bendRate == null) ||
    (charge === "cut" && exc?.processingPerTon == null && cutRate == null) ||
    (charge === "unit" && exc?.unitPrice == null && unitRate == null);
  const material = perTon === null ? 0 : weightTon * perTon;
  const total = material + processing;
  return {
    id: it.id,
    diameter: it.diameter,
    quantity: it.quantity,
    weightTon,
    chargeType: charge,
    materialPerTon: perTon,
    material: round2(material),
    processing: round2(processing),
    processingExact: processing,
    processingRate: processingMissing || charge === "none" ? null : charge === "unit" ? excOr(exc?.unitPrice, unitRate) : excOr(exc?.processingPerTon, charge === "cut" ? cutRate : bendRate),
    processingMissing,
    billingQuantity: charge === "unit" ? it.quantity : weightTon,
    shapeId: it.shapeId,
    shapeName: raw.tool?.name,
    shapeNumber: raw.tool?.tool_number,
    rowNumber: raw.row_number,
    total: round2(total),
    unitPrice: it.quantity ? Math.round((total / it.quantity) * 10000) / 10000 : 0,
    missingPrice: perTon === null || processingMissing,
  };
}

export function transportCost(tons: number, method: TransportMethod, price: number | null, freeAbove: number | null, minimum: number | null): number {
  if (method === "none") return 0;
  if (freeAbove !== null && freeAbove !== undefined && tons >= num(freeAbove)) return 0;
  const raw = method === "fixed" ? num(price) : tons * num(price);
  return round2(Math.max(raw, num(minimum)));
}

export function priceOrder(items: any[], pl: PriceList, customer: CustomerPricing | null, customerId: number | null): PricingResult {
  const priced = items.map((i) => priceItem(i, pl, customer, customerId));
  const sum = (f: (p: PricedItem) => number) => priced.reduce((a, p) => a + f(p), 0);
  const weightTon = sum((p) => p.weightTon);
  const material = sum((p) => p.material);
  const bend = sum((p) => (p.chargeType === "bend" ? p.processing : 0));
  const cut = sum((p) => (p.chargeType === "cut" ? p.processing : 0));
  const units = sum((p) => (p.chargeType === "unit" ? p.processing : 0));

  const custom = customer?.mode === "custom" ? customer : null;
  const useCustomTransport = custom?.transportMode === "custom";
  const transport = transportCost(
    weightTon,
    useCustomTransport ? "per_ton" : pl.transportMethod,
    useCustomTransport && custom?.transportPrice !== null ? custom!.transportPrice : pl.transportPrice,
    useCustomTransport && custom?.transportFreeAboveTons !== null ? custom!.transportFreeAboveTons : pl.transportFreeAboveTons,
    pl.transportMinimum
  );
  const discount = -round2((material * num(custom?.discountPercent)) / 100);
  const subtotal = round2(material + bend + cut + units + transport + discount);
  const vatPercent = num(pl.vatPercent, 18);
  const vat = round2((subtotal * vatPercent) / 100);

  const map = new Map<string, PricingResult["byDiameter"][number]>();
  priced.forEach((p) => {
    const k = String(p.diameter);
    const row = map.get(k) || { diameter: p.diameter, weightTon: 0, material: 0, bend: 0, cut: 0, units: 0, total: 0 };
    row.weightTon += p.weightTon;
    row.material += p.material;
    if (p.chargeType === "bend") row.bend += p.processing;
    if (p.chargeType === "cut") row.cut += p.processing;
    if (p.chargeType === "unit") row.units += p.processing;
    row.total += p.total;
    map.set(k, row);
  });

  return {
    items: priced,
    byDiameter: Array.from(map.values()).sort((a, b) => num(a.diameter) - num(b.diameter)),
    totals: {
      weightTon,
      wasteTon: (weightTon * num(custom?.wastePercent ?? pl.wastePercent)) / 100,
      material: round2(material),
      bend: round2(bend),
      cut: round2(cut),
      units: round2(units),
      transport,
      discount,
      subtotal,
      vatPercent,
      vat,
      total: round2(subtotal + vat),
      missingPrices: priced.filter((p) => p.missingPrice).map((p) => p.id),
    },
  };
}

export function fillMissing(rows: DiameterPrice[], column: "ribbed" | "smooth" | "weldableExtra"): DiameterPrice[] {
  const first = rows.find((r) => r[column] !== null && r[column] !== undefined);
  if (!first) return rows;
  return rows.map((r) => (r[column] === null || r[column] === undefined ? { ...r, [column]: first[column] } : r));
}

export { polygonSteelType };
